import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { endOfDay, startOfDay } from 'date-fns';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import { Panel } from 'primereact/panel';
import { Calendar } from 'primereact/calendar';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AppCard, AppPage } from '../../components/general';
import { toLocalDateTime } from '../../utils/date-helper';
import { TraceContextLogSummaryViewModel } from '../../models/feeTracing';
import { useFeeTracingSearch } from '../../hooks/useFeeTracing';
import styles from './FeeTracingSearch.module.css';

const FeeTracingSearch = () => {
  const navigate = useNavigate();
  const messages = useRef<any>(null);

  const [transactionId, setTransactionId] = useState<string>('');
  const [advancedSearchExpanded, setAdvancedSearchExpanded] = useState<boolean>(false);
  const [loanNumber, setLoanNumber] = useState<string>('');
  const [loanIdentifier, setLoanIdentifier] = useState<string>('');
  
  const now = new Date();
  const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
  const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [startDate, setStartDate] = useState<Date | null>(start);
  const [endDate, setEndDate] = useState<Date | null>(end);


  const [searchEnabled, setSearchEnabled] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<any>({});

  const { searchQuery } = useFeeTracingSearch(searchParams, searchEnabled);

  const handleDirectSearch = () => {
    if (!transactionId || transactionId.trim().length === 0) {
      messages.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter a transaction ID',
        life: 3000,
      });
      return;
    }

    navigate(`/fee-tracing/${transactionId.trim()}`);
  };

  const handleAdvancedSearch = () => {
    const params: any = {};

    if (loanNumber && loanNumber.trim().length > 0) {
      params.loanNumber = loanNumber.trim();
    }
    if (loanIdentifier && loanIdentifier.trim().length > 0) {
      params.loanIdentifier = loanIdentifier.trim();
    }
    if (startDate && endDate) {
      params.startDate = startOfDay(startDate).toISOString();
      params.endDate = endOfDay(endDate).toISOString();
    }

    setSearchParams(params);
    setSearchEnabled(true);
  };

  const [selectedTrace, setSelectedTrace] = useState<TraceContextLogSummaryViewModel | null>(null);

  const handleSelectionChange = (e: any) => {
    const selected = e.value as TraceContextLogSummaryViewModel;
    if (selected?.transactionId) {
      navigate(`/fee-tracing/${selected.transactionId}`);
    }
  };

  const formatDateTime = (rowData: TraceContextLogSummaryViewModel) => {
    return rowData.eventTimestamp ? toLocalDateTime(rowData.eventTimestamp) : '';
  };

  return (
    <AppPage title="Fee Transactions Logs" breadcrumbs="Home - Fee Transactions Logs">
      <Messages ref={messages} />
      
      <AppCard className={styles.feeTracingSearch}>
        <div className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <label htmlFor="transactionId" className="font-semibold">
              Search by Transaction ID
            </label>
            <div className="flex gap-2">
              <InputText
                id="transactionId"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Paste transaction ID here..."
                className="flex-1"
                style={{ backgroundColor: '#f9fafb' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleDirectSearch();
                  }
                }}
              />
              <Button
                label="Go"
                icon="pi pi-search"
                onClick={handleDirectSearch}
                disabled={!transactionId || transactionId.trim().length === 0}
              />
            </div>
          </div>

          <Panel
            header="Advanced Search"
            toggleable
            collapsed={!advancedSearchExpanded}
            onToggle={(e) => setAdvancedSearchExpanded(!e.value)}
          >
            <div className="flex flex-column" style={{ gap: '1rem' }}>
              <div className="flex flex-column gap-2">
                <label htmlFor="loanNumber">Loan Number</label>
                <InputText
                  id="loanNumber"
                  value={loanNumber}
                  onChange={(e) => setLoanNumber(e.target.value)}
                  placeholder="Enter loan number"
                  style={{ backgroundColor: '#f9fafb' }}
                />
              </div>

              <div className="flex flex-column gap-2">
                <label htmlFor="loanIdentifier">Loan Identifier</label>
                <InputText
                  id="loanIdentifier"
                  value={loanIdentifier}
                  onChange={(e) => setLoanIdentifier(e.target.value)}
                  placeholder="Enter loan identifier"
                  style={{ backgroundColor: '#f9fafb' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-column gap-2">
                  <label htmlFor="startDate">Start Date</label>
                  <Calendar
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.value as Date)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    placeholder="Select start date"
                    inputStyle={{ backgroundColor: '#f9fafb' }}
                    iconPos="right"
                  />
                </div>
                
                <div className="flex flex-column gap-2">
                  <label htmlFor="endDate">End Date</label>
                  <Calendar
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.value as Date)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    placeholder="Select end date"
                    inputStyle={{ backgroundColor: '#f9fafb' }}
                    iconPos="right"
                  />
                </div>
              </div>

              <div className="mt-2">
                <Button
                  label="Search"
                  icon="pi pi-search"
                  onClick={handleAdvancedSearch}
                />
              </div>
            </div>
          </Panel>

          {searchEnabled && (
            <div className="flex flex-column gap-2">
              <h3 className="font-semibold text-lg">Search Results</h3>
              {searchQuery.isLoading && <div>Loading...</div>}
              {searchQuery.isError && (
                <div className="text-red-500">
                  Error loading search results: {searchQuery.error instanceof Error ? searchQuery.error.message : 'Unknown error'}
                </div>
              )}
              {searchQuery.data && (
                <PrimeDataTable
                  value={searchQuery.data}
                  selectionMode="single"
                  selection={selectedTrace}
                  onSelectionChange={handleSelectionChange}
                  paginator
                  rows={20}
                  emptyMessage="No results found"
                  className="cursor-pointer"
                >
                  <Column field="eventTimestamp" header="DATE & TIME" body={formatDateTime} sortable />
                  <Column field="transactionId" header="TRANSACTION ID" sortable />
                  <Column field="loanNumber" header="LOAN NUMBER" sortable />
                  <Column field="loanIdentifier" header="LOAN IDENTIFIER" sortable />
                </PrimeDataTable>
              )}
            </div>
          )}
        </div>
      </AppCard>
    </AppPage>
  );
};

export default FeeTracingSearch;
